import React from 'react';
import {
  Server, AlertTriangle, CheckCircle, Bug, Network
} from 'lucide-react';
import { Button } from '../ui/button';

const SurveillanceNetFlowTab = ({ collectorStatus, anomalies, botnetStatus, formatBytes, fetchNetFlowData }) => (
  <div className="space-y-6">
    {/* Collector Status */}
    {collectorStatus && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {collectorStatus.collectors?.map((collector, idx) => (
          <div key={`collector-${idx}`} className="bg-white rounded-xl border shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Server className={`h-5 w-5 ${collector.status === 'active' ? 'text-green-500' : 'text-gray-400'}`} />
                <span className="font-medium">{collector.name}</span>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs ${collector.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{collector.status}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><p className="text-gray-500 text-xs">Type</p><p className="font-medium uppercase">{collector.type}</p></div>
              <div><p className="text-gray-500 text-xs">IP:Port</p><p className="font-medium">{collector.ip}:{collector.port}</p></div>
              <div><p className="text-gray-500 text-xs">{collector.type === 'sflow' ? 'Samples/s' : 'Flows/s'}</p><p className="font-medium">{collector.type === 'sflow' ? collector.samples_per_second : collector.flows_per_second}</p></div>
            </div>
          </div>
        ))}
      </div>
    )}

    {/* Anomalies */}
    {anomalies && (
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-500" />
          Traffic Anomalies ({anomalies.total_anomalies})
        </h3>
        <div className="flex gap-2 mb-4">
          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">Critical: {anomalies.by_severity?.critical || 0}</span>
          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">High: {anomalies.by_severity?.high || 0}</span>
          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">Medium: {anomalies.by_severity?.medium || 0}</span>
        </div>
        <div className="space-y-2">
          {anomalies.anomalies?.slice(0, 5).map((anomaly) => (
            <div key={anomaly.id} className={`p-3 rounded-lg border-l-4 ${anomaly.severity === 'critical' ? 'bg-red-50 border-red-500' : anomaly.severity === 'high' ? 'bg-orange-50 border-orange-500' : 'bg-yellow-50 border-yellow-500'}`}>
              <div className="flex justify-between">
                <div>
                  <p className="font-medium text-sm">{anomaly.type.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-gray-600">{anomaly.description}</p>
                  <p className="text-xs text-gray-500 mt-1">{anomaly.details?.source_ip} &rarr; {anomaly.details?.dest_ip} &bull; {formatBytes(anomaly.details?.bytes_involved || 0)}</p>
                </div>
                <span className={`px-2 py-0.5 h-fit rounded text-xs ${anomaly.status === 'active' ? 'bg-red-200 text-red-700' : anomaly.status === 'investigating' ? 'bg-yellow-200 text-yellow-700' : 'bg-green-200 text-green-700'}`}>{anomaly.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Botnet Detection */}
    {botnetStatus && (
      <div className={`bg-white rounded-xl border shadow-sm p-6 ${botnetStatus.botnet_detected ? 'border-red-300' : ''}`}>
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><Bug className="h-5 w-5 text-red-500" />Botnet Detection</h3>
        {botnetStatus.botnet_detected ? (
          <div className="space-y-3">
            {botnetStatus.detections?.map((detection, idx) => (
              <div key={`detection-${idx}`} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <span className="font-bold text-red-700">{detection.botnet_family.toUpperCase()} Detected</span>
                  <span className="px-2 py-0.5 bg-red-200 text-red-800 rounded text-xs">{detection.risk_level}</span>
                </div>
                <p className="text-sm text-red-600 mb-2">{detection.indicators_found} indicators found &bull; {detection.affected_devices} devices affected</p>
                <div className="mt-2">
                  <p className="text-xs font-medium text-red-800 mb-1">Recommended Actions:</p>
                  <ul className="text-xs text-red-700 space-y-1">
                    {detection.recommended_actions?.map((action, i) => (<li key={action}>&bull; {action}</li>))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div><p className="font-medium text-green-700">No Botnet Activity Detected</p><p className="text-sm text-green-600">{botnetStatus.devices_scanned} devices scanned</p></div>
          </div>
        )}
      </div>
    )}
  </div>
);

export default SurveillanceNetFlowTab;
